// filepath: apps/web/src/design-system/error-states/ValidationError.tsx
//
// FR-UI-VIEW-1 AC-4 / FR-UI-CORE-10 AC-5 (list-level) — 422 validation
// error surface for list / kanban views.
//
// SHAPE
//   Renders a `Banner tone="danger"` header with the localized chrome
//   line, followed by one `Card` per failure row. Each row carries the
//   service-style `code` (machine-readable, exposed via
//   `data-validation-error-code`) plus the user-visible `message`. This
//   mirrors the per-field `InlineFieldError` primitive at the LIST
//   granularity — same ARIA semantics, two-tier composition: the Banner
//   owns the screen-reader announcement (`role="alert" +
//   aria-live="polite"`), each Card owns one discrete row so assistive
//   tech and visual users can scan individual failures.
//
// WHY DISTINCT FROM InlineFieldError
//   `InlineFieldError` is a per-FIELD slot inside the FormRenderer.
//   It assumes the error targets one input element and pairs through
//   `aria-describedby`. The list view / kanban view has no analogous
//   input to pair against — failures are properties of the OUTCOME
//   (e.g. "view_config.kanban.group_by must name an enum field") not of
//   any single input. We therefore render the failures as a flat list
//   of Cards under a single Banner. The Banner carries `role="alert"`
//   so a screen reader announces the existence of the surface; the
//   Cards remain navigable individually via the standard focus model.
//
// INVARIANTS (FR-UI-CORE-10 + FR-UI-OPS-3 carryover)
//   - INV-9 (no dangerouslySetInnerHTML): every message is rendered as
//     a React text node. No HTML interpretation. The fixture in
//     ValidationError.test.tsx asserts the contract by feeding an
//     HTML-ish literal and verifying no `<img>` is created.
//   - All chrome strings flow through `useTranslation()` so the v1
//     locale set (ru/kk/en per FR-UI-CORE-8) renders translated text.
//   - The component takes `errors` as a plain `readonly` array; it
//     does NOT mutate the input. The shape is intentionally
//     "narrows" of the `FieldErrorMap` shape used by `useFormSubmit`:
//     we accept either FieldErrorBody-style rows (when the caller
//     re-uses a per-field envelope) or leaner {code?, message} rows
//     from a list-level validator (FR-UI-VIEW-1 AC-4 — the kanban
//     validator returns a single sentinel string, no per-field
//     envelope today).
//   - When `errors.length === 0`, the component renders `null`. A 422
//     without a rowset degrades to a flat Banner with the chrome line.
//
// FAILURE-MODE NOTE
//   The "validation error" surface is FR-UI-CORE-10's 6th error
//   state, added at FR-UI-VIEW-1 AC-4. Pages route to this component
//   when the server returns HTTP 422 for a non-per-field failure
//   (e.g. PUT record with stale row_version is HTTP 409, not 422; PUT
//   record with an invalid view-config is HTTP 422 today). The
//   EntityListPage owns the status-to-renderer switch — this
//   component is the RENDERED surface; it does not own state.

import React from "react";
import { Banner } from "@/design-system/Banner";
import { Card } from "@/design-system/Card";
import { useTranslation } from "@/i18n/useTranslation";

/**
 * A single row in the validation-error surface. The shape is a
 * narrow subset of `FieldErrorBody` so callers that already parse a
 * 422 envelope can pass through without rewriting.
 */
export interface ValidationErrorRow {
  /**
   * Optional field id the row refers to (e.g. the field that failed
   * per-field validation). Omitted for list-level failures
   * (FR-UI-VIEW-1 AC-4). When present, surfaced via
   * `data-validation-error-field` for tests and future analytics.
   */
  field?: string;
  /**
   * Machine-readable error class (e.g. "invalid_kanban_group_by",
   * "required"). Rendered as the card's heading + surfaced via
   * `data-validation-error-code`. When absent, the chrome "Error" label
   * is used as a fallback.
   */
  code?: string;
  /**
   * Localized error message. Plain strings render as-is.
   */
  message: string;
}

export interface ValidationErrorProps {
  /**
   * The validated failure rows. When the array is empty the component
   * renders the chrome Banner WITHOUT any per-row cards (single-line
   * summary). When `undefined`, the component renders `null`.
   */
  errors?: readonly ValidationErrorRow[];
  /**
   * Optional retry callback. Surfaces a "Retry" action on the header
   * Banner (mirrors the FR-UI-CORE-10 retry contract used by the
   * list/kanban fetch path). When omitted, no retry action is shown.
   */
  onRetry?: () => void;
  /** Test hook forwarded to the outer surface root. */
  "data-testid"?: string;
}

/**
 * ValidationError — list-level 422 surface.
 *
 * Renders:
 *   <Banner tone="danger" role="alert">
 *     {chrome line: "Validation failed"}
 *     [optional Retry button]
 *   </Banner>
 *   <Card validation-error-row ...>code + message</Card> × N
 *
 * @example
 *   <ValidationError
 *     errors={[
 *       {
 *         code: "invalid_kanban_group_by",
 *         message: "view_config.kanban.group_by must name an enum field",
 *       },
 *     ]}
 *     onRetry={() => refetch()}
 *   />
 */
export function ValidationError({
  errors,
  onRetry,
  "data-testid": testId = "validation-error",
}: ValidationErrorProps): React.ReactElement | null {
  const { t } = useTranslation();
  // Defensive: the hook returns the key string when missing; the
  // chrome line is the canonical entry-point message for screen
  // readers (Banner already carries role="alert" + aria-live="polite"
  // — see Banner.tsx).
  const chrome = t("validation.error.list", "Validation failed");
  const retryLabel = t("common.retry", "Retry");

  if (errors === undefined) return null;

  // Single empty-array fallback: surface a banner-only message so the
  // user receives SOME signal (a 422 with a parsed-empty envelope is
  // unlikely but defensive). Without the fallback, an empty array
  // would render `null` and silence the screen-reader announcement.
  if (errors.length === 0) {
    return (
      <Banner
        tone="danger"
        data-testid={`${testId}--header`}
        aria-label={chrome}
      >
        {chrome}
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="ds-validation-error__retry"
            data-testid={`${testId}--retry`}
            aria-label={retryLabel}
            style={{
              marginLeft: "12px",
              border: "none",
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              textDecoration: "underline",
            }}
          >
            {retryLabel}
          </button>
        ) : null}
      </Banner>
    );
  }

  return (
    <section
      data-testid={testId}
      data-validation-error-row-count={errors.length}
      aria-label={chrome}
      aria-live="polite"
      style={{ display: "flex", flexDirection: "column", gap: "12px" }}
    >
      <Banner
        tone="danger"
        data-testid={`${testId}--header`}
        aria-label={chrome}
      >
        {chrome}
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="ds-validation-error__retry"
            data-testid={`${testId}--retry`}
            aria-label={retryLabel}
            style={{
              marginLeft: "12px",
              border: "none",
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              textDecoration: "underline",
            }}
          >
            {retryLabel}
          </button>
        ) : null}
      </Banner>
      {errors.map((row, idx) => {
        // Stable per-row testid — uses (field || code || index) so
        // tests can target a specific row without coupling to array
        // position alone. Mirrors InlineFieldError's per-id testid.
        const slotId =
          row.field !== undefined && row.field !== ""
            ? row.field
            : row.code !== undefined && row.code !== ""
              ? row.code
              : String(idx);
        const codeLabel =
          row.code ??
          t("validation.error.code_default", "Field contains an error");
        return (
          <Card
            key={`${slotId}--${idx}`}
            padding="md"
            role="alert"
            data-testid={`${testId}-row-${slotId}`}
            data-validation-error-code={row.code}
            data-validation-error-field={row.field}
            aria-label={codeLabel}
          >
            <strong
              className="ds-validation-error__code"
              style={{
                display: "block",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--text-xs)",
                color: "var(--color-danger-subtle-text)",
                marginBottom: "4px",
              }}
            >
              {codeLabel}
            </strong>
            {/* INV-9: message is a React text node. No HTML. */}
            <span className="ds-validation-error__message">{row.message}</span>
          </Card>
        );
      })}
    </section>
  );
}
